<template>
  <transition name="atd">
    <div v-if="visible" class="fixed inset-0 bg-gray-600/50 flex items-center justify-center z-50" @click="$emit('close')">
      <div class="atd-panel" @click.stop>
        <div class="atd-head">
          <div>
            <h3 class="atd-title">Add to dashboard</h3>
            <p class="atd-sub">Places <strong>{{ sheetName || 'this sheet' }}</strong> on a dashboard in Insights. It reads the workflow's materialized output, so it stays current after each run.</p>
          </div>
          <button class="btn-icon" title="Close" @click="$emit('close')">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <div class="atd-body">
          <LoadingIndicator v-if="loading" message="Loading your dashboards…" />
          <template v-else>
            <p class="atd-label">Your dashboards</p>
            <div v-if="dashboards.length" class="atd-list" role="radiogroup">
              <label v-for="d in dashboards" :key="d.id" class="atd-option" :class="{ 'is-on': choice === d.id }">
                <input v-model="choice" type="radio" :value="d.id" />
                <span class="atd-name">{{ d.title }}</span>
                <span class="atd-count">{{ Math.max(d.placements - 1, 0) }} {{ d.placements - 1 === 1 ? 'chart' : 'charts' }}</span>
              </label>
            </div>
            <p v-else class="atd-empty">You don't have a dashboard yet.</p>
            <label class="atd-option" :class="{ 'is-on': choice === 'new' }">
              <input v-model="choice" type="radio" value="new" />
              <span class="atd-name">New dashboard</span>
            </label>
            <input v-if="choice === 'new'" ref="title" v-model.trim="title" class="atd-input" placeholder="Dashboard title" @keydown.enter="submit" />
            <p v-if="error" class="atd-error">{{ error }}</p>
          </template>
        </div>

        <div class="atd-foot">
          <button class="btn-secondary" @click="$emit('close')">Cancel</button>
          <button class="btn-accent" :disabled="!canSubmit" @click="submit">{{ saving ? 'Adding…' : 'Add chart' }}</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

const PAGES_URL = '/api/impact_analytics/agent-pages/';

export default {
  name: 'AddToDashboardModal',
  components: { LoadingIndicator },
  props: {
    visible: { type: Boolean, default: false },
    workflowId: { type: Number, default: null },
    sheetId: { type: String, default: '' },
    sheetName: { type: String, default: '' },
  },
  emits: ['close', 'added'],
  data() {
    return { dashboards: [], loading: false, saving: false, choice: null, title: '', error: '' };
  },
  computed: {
    canSubmit() {
      if (this.saving || !this.choice) return false;
      return this.choice !== 'new' || !!this.title;
    },
  },
  watch: {
    visible(open) {
      if (open) this.load();
    },
    choice(value) {
      if (value === 'new') this.$nextTick(() => this.$refs.title && this.$refs.title.focus());
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      this.title = '';
      const { data } = await axios.get(PAGES_URL, { params: { category: 'insights_dashboards' } });
      this.dashboards = data.filter((d) => d.mine);
      this.choice = this.dashboards.length ? this.dashboards[0].id : 'new';
      this.loading = false;
    },
    async submit() {
      if (!this.canSubmit) return;
      this.saving = true;
      this.error = '';
      try {
        let page = this.dashboards.find((d) => d.id === this.choice);
        if (this.choice === 'new') {
          const { data } = await axios.post(PAGES_URL, { title: this.title });
          page = data;
        }
        await axios.post(`${PAGES_URL}${page.id}/add-sheet/`, { workflow_id: this.workflowId, sheet_id: this.sheetId });
        this.$emit('added', page);
      } catch (error) {
        this.error = error.response?.data?.error || 'Could not add the chart.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.atd-panel { width: 100%; max-width: 480px; background: #FFFFFF; border-radius: 8px; box-shadow: 0 10px 30px rgba(30, 31, 33, .18); }
.atd-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 20px 20px 12px; border-bottom: 1px solid #E8E9E9; }
.atd-title { margin: 0; font-size: 16px; font-weight: 600; color: #1E1F21; }
.atd-sub { margin: 4px 0 0; font-size: 12px; line-height: 1.5; color: #626264; }
.atd-sub strong { color: #353537; }
.atd-body { padding: 16px 20px; display: flex; flex-direction: column; gap: 8px; max-height: 50vh; overflow: auto; }
.atd-label { margin: 0; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #626264; }
.atd-list { display: flex; flex-direction: column; gap: 4px; }
.atd-option { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border: 1px solid #E8E9E9; border-radius: 8px; cursor: pointer; font-size: 13px; color: #353537; }
.atd-option.is-on { border-color: #F76734; background: #FFF7F4; }
.atd-option input { accent-color: #F76734; }
.atd-name { flex: 1; font-weight: 500; color: #1E1F21; }
.atd-count { font-size: 12px; color: #626264; font-variant-numeric: tabular-nums; }
.atd-empty { margin: 0; font-size: 12px; color: #626264; }
.atd-input { border: 1px solid #DDDDDD; border-radius: 8px; padding: 8px 12px; font-size: 13px; }
.atd-input:focus { outline: 2px solid #4573D2; outline-offset: 1px; }
.atd-error { margin: 0; font-size: 12px; color: #D53D3D; }
.atd-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 20px 16px; border-top: 1px solid #E8E9E9; }
.atd-enter-active, .atd-leave-active { transition: opacity .15s ease; }
.atd-enter-from, .atd-leave-to { opacity: 0; }
</style>
